import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { FaVideo, FaMicrophone, FaComments, FaCheck, FaSignOutAlt } from "react-icons/fa";
import { SkeletonRoom } from "../../Skeleton";
import API_URL from '../../../utils/api';
import "./VirtualRoom.css";
export default function VirtualRoom() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [currentSpeakerIndex, setCurrentSpeakerIndex] = useState(0);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [promptCards, setPromptCards] = useState([
    "Share a time when you felt truly happy. What made that moment special?",
    "Describe a challenge you've overcome. What did you learn from it?"
  ]);
  const [userId] = useState(() => {
    const id = sessionStorage.getItem("userId") || localStorage.getItem("userId");
    console.log(`[VirtualRoom] 🔑 Retrieved userId from sessionStorage:`, id);
    return id;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [leaveNotifications, setLeaveNotifications] = useState([]);
  const socketRef = useRef(null);
  // Fetch session data
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await fetch(`${API_URL}/api/sessions/${sessionId}`);
        const data = await response.json();
        // Parse members if it's a string
        if (data.members && typeof data.members === 'string') {
          data.members = JSON.parse(data.members);
        }
        setSession(data);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        }
    };
    fetchSession();
  }, [sessionId]);

  // Fetch AI-generated questions for this session
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await fetch(`${API_URL}/api/sessions/${sessionId}/ai-questions`);
        const data = await res.json();
        if (data.facilitation && data.facilitation.length > 0) {
          setPromptCards(data.facilitation.slice(0, 2));
        }
      } catch {
        // Keep fallback questions already set in initial state
      } finally {
        setQuestionsLoading(false);
      }
    };
    fetchQuestions();
  }, [sessionId]);
  // Socket.io for real-time synchronization
  useEffect(() => {
    if (!socketRef.current) {
      socketRef.current = io(API_URL, { transports: ["websocket"] });
      
      socketRef.current.on("connect", () => {
        socketRef.current.emit("join-session", sessionId);
        // Track user so server knows who is in this session
        const userName = sessionStorage.getItem("userName") || localStorage.getItem("userName") || "Unknown";
        socketRef.current.emit("track-session", { sessionId, userId, userName });
      });

      // Show notification when someone leaves
      socketRef.current.on(`session-${sessionId}-user-left`, (data) => {
        const name = data?.user?.name || "Someone";
        const msg = `${name} left the session`;
        setLeaveNotifications(prev => [...prev, { id: Date.now(), msg }]);
        // Auto-remove after 5 seconds
        setTimeout(() => {
          setLeaveNotifications(prev => prev.filter(n => n.msg !== msg));
        }, 5000);
      });
      
      // Listen for speaker/card changes
      socketRef.current.on(`session-${sessionId}-next-speaker`, (data) => {
        
        setCurrentCardIndex(data.cardIndex);
        setCurrentSpeakerIndex(data.speakerIndex);
        
        // Force a re-render by updating a timestamp
        setSession(prev => ({ ...prev, _lastUpdate: Date.now() }));
      });
    }
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [sessionId, userId]);
  const handleLeaveSession = async () => {
    if (!window.confirm("Are you sure you want to leave the session? Other participants will be notified.")) return;
    try {
      const userName = sessionStorage.getItem("userName") || localStorage.getItem("userName") || "Unknown";
      await fetch(`${API_URL}/api/sessions/${sessionId}/leave`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, userName })
      });
      if (socketRef.current) {
        socketRef.current.emit("untrack-session");
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    } catch (err) {
    }
    navigate("/dashboard");
  };

  const handleDone = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}/virtual-room/done`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, cardIndex: currentCardIndex })
      });
      const data = await response.json();
      if (data.ok) {
        // Socket event will update the UI
      } else {
        alert(data.message || "It's not your turn to share yet!");
      }
    } catch (error) {
      }
  };
  const isSessionComplete = !questionsLoading && currentCardIndex >= promptCards.length;
  // Check if I-sharing is complete and transition to pre-breakout evaluation
  useEffect(() => {
    if (isSessionComplete && session) {
      // Small delay to show completion message, then redirect
      const timer = setTimeout(() => {

        if (socketRef.current) {
          socketRef.current.emit('untrack-session');
        }
        navigate(`/pre-breakout-evaluation/${sessionId}`);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isSessionComplete, sessionId, navigate, session, questionsLoading]);
  if (!session || isLoading || questionsLoading) {
    return (
      <div className="virtual-room-root">
        <SkeletonRoom />
      </div>
    );
  }
  const members = session.members || [];
  const currentSpeaker = members[currentSpeakerIndex] || {};
  
  // Improved turn detection with type normalization
  const currentSpeakerUserId = String(currentSpeaker.user_id || currentSpeaker.userId || '');
  const myUserId = String(userId || '');
  const isMyTurn = currentSpeakerUserId !== '' && myUserId !== '' && currentSpeakerUserId === myUserId;
  
  
  const currentPrompt = promptCards[currentCardIndex] || "I-sharing complete! Moving to breakout rooms...";
  return (
    <div className="virtual-room-root">
      {/* Leave notifications */}
      <div className="leave-notifications">
        {leaveNotifications.map(n => (
          <div key={n.id} className="leave-notification-toast">
            🚪 {n.msg}
          </div>
        ))}
      </div>

      {/* Video Call Link at Top */}
      <div className="virtual-room-meet-section">
        <div className="virtual-room-meet-label"><FaVideo /> Video Call:</div>
        <a 
          href={session.meet_link} 
          target="_blank" 
          rel="noopener noreferrer"
          className="virtual-room-meet-link"
          onClick={(e) => { e.preventDefault(); if (session?.meet_link) window.open(session.meet_link, '_blank', 'noopener,noreferrer'); }}
        >
          Join or Return to Call
        </a>
      </div>
      {/* Session Header */}
      <div className="virtual-room-header">
        <h2>{session.name} - Virtual Room</h2>
        <div className="header-right">
          <div className="card-progress">
            Card {Math.min(currentCardIndex + 1, promptCards.length)} of {promptCards.length}
          </div>
          <button className="leave-session-btn" onClick={handleLeaveSession}>
            <FaSignOutAlt /> Leave Session
          </button>
        </div>
      </div>
      {!isSessionComplete ? (
        <>
          {/* Current Speaker Indicator */}
          <div className="current-speaker-section">
            <div className="speaker-label">
              {isMyTurn ? <><FaMicrophone /> It's your turn to share!</> : <><FaMicrophone /> {currentSpeaker.name}'s turn to share</>}
            </div>
          </div>
          {/* Prompt Card */}
          <div className="prompt-card">
            <div className="prompt-icon"><FaComments /></div>
            <div className="prompt-text">{currentPrompt}</div>
          </div>
          {/* Participants Status */}
          <div className="participants-status">
            <div className="status-header">Participants</div>
            <div className="participants-grid">
              {members.map((member, index) => {
                const isSpeaker = index === currentSpeakerIndex;
                return (
                  <div 
                    key={index} 
                    className={`participant-badge ${isSpeaker ? 'speaker' : ''}`}
                  >
                    <div className="participant-avatar-small">
                      {member.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="participant-name-small">{member.name}</div>
                    {isSpeaker && <span className="speaker-indicator"><FaMicrophone /></span>}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Done Button */}
          <div className="virtual-room-actions">
            {isMyTurn ? (
              <button 
                className="done-btn"
                onClick={handleDone}
              >
                I'm Done Sharing
              </button>
            ) : (
              <div className="waiting-message">
                Waiting for {currentSpeaker.name} to finish sharing...
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="session-complete">
          <div className="complete-icon"><FaCheck size={48} /></div>
          <h3>Session Complete!</h3>
          <p>Thank you all for participating and sharing. Great connections made today!</p>
        </div>
      )}
    </div>
  );
}
